import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const ArticleAdminPanel = () => {
  const [articles, setArticles] = useState([]);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/article-applications`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
        // console.log(data);
      } else {
        console.error("Failed to fetch article applications");
      }
    } catch (error) {
      console.error("Error fetching article applications:", error);
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/approve-article/${id}`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (response.ok) {
        fetchArticles(); // Refresh the list
      } else {
        console.error("Failed to approve article");
      }
    } catch (error) {
      console.error("Error approving article:", error);
    }
  };

  const handleReject = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/reject-article/${id}`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (response.ok) {
        fetchArticles(); // Refresh the list
      } else {
        console.error("Failed to reject article");
      }
    } catch (error) {
      console.error("Error rejecting article:", error);
    }
  };

  const toggleExpand = (id) => {
    setExpandedArticle(expandedArticle === id ? null : id);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const handleContributorApp = () => {
    navigate("/admin");
  };

  const statusColor = (status) => {
    if (status === 0) return "bg-[#feca57] text-black"; // pending
    if (status === 1) return "bg-[#ff9ff3] text-black"; // approved
    return "bg-[#ff6b6b] text-white"; // rejected
  };

  return (
    <div className="article-admin-panel text-[#333]">
      <header className="admin-header flex justify-between items-center p-5 bg-[#f8f8f8] shadow-[0_2px_4px_rgba(0,0,0,0.1)] max-[768px]:flex-col max-[768px]:items-start">
        <img src="/logo.png" alt="Sports Hub Logo" className="logo h-10" />
        <div className="header-buttons flex items-center gap-3 max-[768px]:mt-2.5">
          <button
            onClick={handleContributorApp}
            className="contributor-app-btn bg-[#3498db] text-white py-2 px-5 rounded-[5px] transition-colors duration-300 hover:bg-[#2980b9]"
          >
            Contributor Applications
          </button>
          <button
            onClick={handleLogout}
            className="logout-btn bg-[#ffc107] text-white py-2 px-5 rounded-[5px] transition-colors duration-300 hover:bg-[#ff6b81] max-[768px]:mt-2.5"
          >
            Logout
          </button>
        </div>
      </header>
      <main className="admin-main p-5">
        <h1 className="text-[#2f3542] mb-5 text-2xl">Article Applications</h1>
        <div className="table-container overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-[0_0_20px_rgba(0,0,0,0.1)]">
            <thead>
              <tr>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Title</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Category</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Author</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Status</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <React.Fragment key={article.article_id}>
                  <tr
                    onClick={() => toggleExpand(article.article_id)}
                    className="hover:bg-[#f5f6fa] border-b border-[#ddd]"
                  >
                    <td className="p-[15px] text-left">{article.title}</td>
                    <td className="p-[15px] text-left">{article.category}</td>
                    <td className="p-[15px] text-left">{article.author_name}</td>
                    <td className="p-[15px] text-left">
                      <span
                        className={`status inline-block px-2 py-1 rounded-full text-[0.8em] font-bold ${statusColor(
                          article.status
                        )}`}
                      >
                        {article.status === 0
                          ? "Pending"
                          : article.status === 1
                          ? "Approved"
                          : "Rejected"}
                      </span>
                    </td>
                    <td className="p-[15px] text-left">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(article.article_id);
                        }}
                        className="approve-btn bg-[#1dd1a1] text-white py-2 px-4 rounded-[5px] mr-2 transition-colors duration-300 hover:bg-[#10ac84]"
                      >
                        Approve
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(article.article_id);
                        }}
                        className="reject-btn bg-[#ff6b6b] text-white py-2 px-4 rounded-[5px] transition-colors duration-300 hover:bg-[#ee5253]"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                  {expandedArticle === article.article_id && (
                    <tr className="border-b border-[#ddd]">
                      <td colSpan="5" className="p-[15px] text-left">
                        <div className="expanded-details bg-[#f1f2f6] p-5 rounded-[5px] mt-2">
                          <h3 className="font-semibold mb-2">Article Details</h3>
                          <p>
                            <strong>Main Heading:</strong> {article.main_heading}
                          </p>
                          <p>
                            <strong>Description:</strong> {article.description}
                          </p>
                          <h4 className="font-semibold mt-3">Sub-headings:</h4>
                          {article.sub_headings.map((subHeading, index) => (
                            <div key={index} className="sub-heading bg-white p-[15px] mb-2.5 rounded-[5px] shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
                              <p>
                                <strong>Title:</strong> {subHeading.title}
                              </p>
                              <p>
                                <strong>Content:</strong> {subHeading.content}
                              </p>
                            </div>
                          ))}
                          <h4 className="font-semibold mt-3">References:</h4>
                          <ul className="list-disc pl-5">
                            {article.references.map((reference, index) => (
                              <li key={index}>{reference}</li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default ArticleAdminPanel;
